import { useId, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { cn } from '../lib/cn'

/**
 * SMS one-time-code entry.
 *
 * Six separate boxes visually, but a single logical field: `autocomplete`
 * carries `one-time-code` so the browser can offer the code straight from the
 * SMS, which matters a great deal on a phone-first product in a market where
 * typing a code is the main friction in signup.
 */
export type OtpInputProps = {
  value: string
  onChange: (value: string) => void
  length?: number
  label?: string
  error?: string | undefined
  disabled?: boolean
  /** Called when the last digit is entered — usually to auto-submit. */
  onComplete?: ((value: string) => void) | undefined
}

export function OtpInput({
  value,
  onChange,
  length = 6,
  label = 'Code de vérification',
  error,
  disabled = false,
  onComplete,
}: OtpInputProps) {
  const groupId = useId()
  const refs = useRef<(HTMLInputElement | null)[]>([])

  const digits = Array.from({ length }, (_, i) => value[i] ?? '')

  function commit(next: string) {
    const clean = next.replace(/\D/g, '').slice(0, length)
    onChange(clean)
    if (clean.length === length) onComplete?.(clean)
  }

  function setDigit(index: number, digit: string) {
    const next = digits.slice()
    next[index] = digit
    commit(next.join(''))
    if (digit && index < length - 1) refs.current[index + 1]?.focus()
  }

  function onKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Backspace on an empty box steps back, which is what people expect.
      refs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && index > 0) refs.current[index - 1]?.focus()
    if (e.key === 'ArrowRight' && index < length - 1) refs.current[index + 1]?.focus()
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text')
    commit(pasted)
    const target = Math.min(pasted.replace(/\D/g, '').length, length - 1)
    refs.current[target]?.focus()
  }

  return (
    <fieldset className="flex flex-col gap-2" disabled={disabled}>
      {/* The frame shows the boxes alone; the legend still has to exist for a
          screen reader, so it is hidden visually rather than dropped. */}
      <legend id={groupId} className="sr-only">
        {label}
      </legend>
      <div
        className="flex justify-between gap-2"
        role="group"
        aria-labelledby={groupId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${groupId}-error` : undefined}
      >
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el
            }}
            value={digit}
            onChange={(e) => setDigit(i, e.target.value.replace(/\D/g, '').slice(-1))}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={onPaste}
            inputMode="numeric"
            // Only the first box carries the autocomplete hint; repeating it
            // makes some browsers fill every box with the whole code.
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            aria-label={`Chiffre ${i + 1} sur ${length}`}
            // Figma: 43x44 boxes, 8px apart, slate-100, no border.
            className={cn(
              'h-11 w-full rounded-md text-center text-lg font-medium',
              'focus-visible:outline-brand-600 text-slate-900',
              // The cell you are on grows very slightly and the ones you have
              // filled hold a brand ring, so the code visibly builds up rather
              // than six identical boxes quietly gaining characters.
              'transition-[background-color,box-shadow,transform] duration-[--duration-fast] ease-[--ease-out-soft]',
              'focus-visible:scale-105',
              error
                ? 'bg-rose-50 ring-1 ring-rose-300'
                : value[i]
                  ? 'ring-brand-300 bg-white ring-1'
                  : 'bg-slate-100',
              'disabled:text-slate-400',
            )}
          />
        ))}
      </div>
      {error ? (
        <p id={`${groupId}-error`} role="alert" className="text-danger animate-slide-up text-xs">
          {error}
        </p>
      ) : null}
    </fieldset>
  )
}
